import './index.sass';

import 'core-js/es';
import 'url-search-params-polyfill';

import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import { RouterProvider } from 'react-router5';
import { Provider as StoreProvider } from 'react-redux';
import { initializeApp } from 'firebase/app';
// import amplitude from 'amplitude-js';
import { getAnalytics } from 'firebase/analytics';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import axios from 'axios';
import WebApp from '@twa-dev/sdk';
import * as Sentry from '@sentry/react';

import App from './core/App';
import { store } from '$store/store';
import { TranslatorProvider } from '$translation';
import router from 'navigation/router';
import {
  ensureVisitorId,
  getStorageItem,
  getUtmOffline,
  getUtmOnline,
  isPWA,
  isPromoLinkToken,
  postPromoLinkVisit,
  sendRef,
  setAnalytics,
  setApiUrl,
  setStorageItem,
  setTokenRaw,
  setUtmLocal,
  setUtmServer,
  statReachGoal,
  getTelegramBotId,
} from '$utils';
import {
  FIREBASE_CONFIG,
  IS_DEVELOPMENT,
  IS_PRODUCTION,
  LanguageTags,
  SENTRY_DSN,
} from '$shared/constants';
import { ActionSheetProvider } from '$uikit/ActionSheet/ActionSheet';
import { authActions } from '$store/auth';
import { mainActions } from '$store/main';
import { install } from 'resize-observer';
import { Button, Placeholder } from '$uikit';
import { useCurrentLang, useTranslator } from '$hooks';
import { NotFound } from '$core';
import { fbInit } from '$utils/facebook';

if (!IS_DEVELOPMENT) {
  Sentry.init({
    dsn: SENTRY_DSN,
    defaultIntegrations: false,
    integrations: [
      Sentry.dedupeIntegration(),
      Sentry.functionToStringIntegration(),
      Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
    ],
    tracesSampleRate: 0,
    sampleRate: 1.0,
    maxBreadcrumbs: 100,
    beforeSend(event) {
      if (event.tags?.feature === 'chat-ws') {
        return event;
      }
      const isUnhandled = event.exception?.values?.some(
        (v) => v.mechanism?.handled === false,
      );
      return isUnhandled ? event : null;
    },
  });
}

const initialUrl = location.href;
const initialPath = location.pathname;
window.isMedia = location.hostname === 'tonplace.io';
window.sessionStartedAt = Date.now();
window.isWebApp =
  WebApp.initDataUnsafe &&
  !!WebApp.initDataUnsafe.hash &&
  WebApp.initDataUnsafe.hash.length > 0;

if (window.isWebApp) {
  WebApp.ready();
  WebApp.expand();
  WebApp.disableVerticalSwipes();
}

try {
  const perfObserver = new PerformanceObserver((entries) => {
    const { domInteractive, duration }: any = entries.getEntriesByType('navigation')[0];
    if (duration) {
      statReachGoal(`page_loaded`, {
        domLoadMs: domInteractive.toFixed(),
        pageLoadMs: duration.toFixed(),
      });
    }
  });
  perfObserver.observe({ type: 'navigation' });
} catch (e) {
  console.error(e);
}

if (!IS_PRODUCTION) {
  const url = new URL(location.href);

  const apiUrl = url.searchParams.get('apiUrl');
  if (apiUrl) {
    url.searchParams.delete('apiUrl');
    setApiUrl(apiUrl);
  }

  const token = url.searchParams.get('token');
  if (token && !url.pathname.startsWith('/email/bind/confirm')) {
    url.searchParams.delete('token');
    setTokenRaw(token);
  }

  const resultUrl = url.toString();
  if (resultUrl !== location.href) {
    history.pushState(null, '', resultUrl);
  }
}

// amplitude.getInstance().init('f634775b4b835d00f20cab9aeac30b3c', undefined, {
//   saveEvents: true,
//   includeUtm: true,
//   includeReferrer: true,
//   batchEvents: true,
// });

// Может вызваться в <head>, если яндекс загрузится быстрее чем бандл
document.addEventListener('yacounter87337659inited', () => {
  // @ts-ignore
  window.isYAInited = true;
  statReachGoal('ya_inited');
});

// @ts-ignore
window.initialInnerHeight = window.innerHeight;

// Initialize Firebase
try {
  const app = initializeApp(FIREBASE_CONFIG);
  const analytics = getAnalytics(app);
  setAnalytics(analytics);
} catch (e) {}

const queryParams = new URLSearchParams(window.location.search || '');
const tgAuth: any = {};
const url = new URL(location.href);

if (queryParams.get('isolated')) {
  setStorageItem('isolated', '1');
}

if (queryParams.get('ntype') && +queryParams.get('ntype')! > 0) {
  setStorageItem(
    'notifyTracking',
    `${queryParams.get('ntype')},${queryParams.get('nextra')}`,
  );
}

// Маппинг коротких имен параметров в полные
const paramMapping: Record<string, string> = {
  us: 'utm_source',
  uc: 'utm_campaign',
  u: 'utm', // альтернативный вариант для utm_campaign
  vi: 'visitor_id',
  ep1: 'extra_param1',
  ep2: 'extra_param2',
  ep3: 'extra_param3',
};

// Применяем маппинг к URL параметрам
Object.entries(paramMapping).forEach(([shortKey, fullKey]) => {
  const value = queryParams.get(shortKey);
  if (value && !queryParams.has(fullKey)) {
    queryParams.set(fullKey, value);
  }
});

const parseStartParamWithQuery = (
  startParam: string,
): { main: string; params: Record<string, string> } => {
  // Защита от пустых или некорректных значений
  if (!startParam || typeof startParam !== 'string') {
    return { main: '', params: {} };
  }

  try {
    // Проверяем, начинается ли строка с параметра (содержит '=')
    const firstPart = startParam.split('_')[0];
    const isFirstPartParam = firstPart.includes('=');

    let main = '';
    let paramsString = startParam;

    if (!isFirstPartParam) {
      // Если первая часть не параметр, то это основная команда
      const parts = startParam.split('_');
      main = parts[0];
      paramsString = parts.slice(1).join('_');
    }

    const params: Record<string, string> = {};

    // Парсим параметры, если они есть
    if (paramsString && (isFirstPartParam || paramsString.length > 0)) {
      // Заменяем _ на & для URLSearchParams
      const searchParams = new URLSearchParams(paramsString.replace(/_/g, '&'));

      searchParams.forEach((value, key) => {
        // Дополнительная проверка на валидность ключа и значения
        if (key && value) {
          // Маппим короткие имена в полные
          const fullKey = paramMapping[key] || key;
          params[fullKey] = value;
        }
      });
    }

    return { main, params };
  } catch (error) {
    console.error('Error parsing start_param:', error);
    return { main: '', params: {} };
  }
};

if (window.isWebApp && WebApp.initDataUnsafe.start_param) {
  const { main: p, params: additionalParams } = parseStartParamWithQuery(
    WebApp.initDataUnsafe.start_param,
  );

  // Добавляем дополнительные параметры в queryParams для обработки в initUt()
  Object.entries(additionalParams).forEach(([key, value]) => {
    try {
      queryParams.set(key, value);
    } catch (e) {
      console.error('Error setting query param:', key, value, e);
    }
  });

  let changeUrl = true;
  let matches: any = null;
  if (p === 'mine_explore') {
    url.pathname = '/mine/explore';
  } else if (p === 'mine') {
    url.pathname = '/mine';
  } else if (p === 'clips') {
    url.pathname = '/clips';
  } else if ((matches = p.match(/^clips(\d+)$/))) {
    url.pathname = `/id${matches[1]}?tab=clips`;
  } else if ((matches = p.match(/^gift(\d+)$/))) {
    url.pathname = `/id${matches[1]}`;
    url.searchParams.set('w', `gift${matches[1]}`);
  } else if ((matches = p.match(/^id(\d+)$/))) {
    url.pathname = `/id${matches[1]}`;
  } else if ((matches = p.match(/^u(\d+)-([a-z0-9]{2,32}-[0-9A-Za-z]{6})$/))) {
    // Промо-ссылка: startapp=u<ownerId>-<token>. Расширяет обычную u<id>, но ref
    // ставим сразу — ветка /u/ ниже читает initialPath, снятый до этого pushState,
    // а в mini-app путь при загрузке всегда "/", поэтому refDomain там не пишется.
    const refId = +matches[1];
    if (refId > 0) {
      setStorageItem('ref', new Number(refId).toString(16));
    }
    url.pathname = `/u/id${matches[1]}`;
    url.searchParams.set('source', matches[2]);
    // initUt() читает queryParams, а не URL.
    queryParams.set('source', matches[2]);
  } else if ((matches = p.match(/^u(\d+)$/))) {
    url.pathname = `/u/id${matches[1]}`;
  } else if ((matches = p.match(/^m(\d+)$/))) {
    url.pathname = `/m/id${matches[1]}`;
  } else if ((matches = p.match(/^squad(\d+)_(\d+)$/))) {
    const refId = +matches[2];
    if (refId > 0) {
      setStorageItem('ref', new Number(refId).toString(16));
    }
    url.pathname = `/mine/squad/${matches[1]}`;
  } else if (p === 'customTip') {
    const authorId = additionalParams.authorId;

    if (!authorId) {
      changeUrl = false;
    } else {
      url.pathname = `/custom_tips/${authorId}`;

      if (additionalParams.amount)
        url.searchParams.set('amount', additionalParams.amount);
      if (additionalParams.message)
        url.searchParams.set('message', additionalParams.message);
      if (additionalParams.method)
        url.searchParams.set('method', additionalParams.method);
    }
  } else if (p === 'leaderboard') {
    url.pathname = '/leaderboard';
  } else if (p === 'balance') {
    url.pathname = `/other`;
    url.searchParams.set('w', 'balance');
  } else if (p.startsWith('r-')) {
    const str = WebApp.initDataUnsafe.start_param!.slice(2);
    const firstEq = str.indexOf('=');
    const paramStart = firstEq > -1 ? str.lastIndexOf('_', firstEq) : -1;
    const routeName = paramStart > -1 ? str.slice(0, paramStart) : str;
    const routeParams =
      paramStart > -1
        ? Object.fromEntries(
            new URLSearchParams(str.slice(paramStart + 1).replace(/_/g, '&')),
          )
        : {};

    try {
      const path = router.buildPath(routeName, routeParams);
      if (path) {
        const builtUrl = new URL(path, url.origin);
        url.pathname = builtUrl.pathname;
        builtUrl.searchParams.forEach((v, k) => url.searchParams.set(k, v));
      } else {
        changeUrl = false;
      }
    } catch (e) {
      changeUrl = false;
    }
  } else {
    changeUrl = false;
  }

  if (changeUrl) {
    history.pushState(null, '', url);
  }
} else if (queryParams.get('hash') && queryParams.get('auth_date')) {
  const values = [
    'id',
    'first_name',
    'username',
    'last_name',
    'photo_url',
    'auth_date',
    'hash',
  ];
  values.forEach((key) => {
    tgAuth[key] = queryParams.get(key);
    url.searchParams.delete(key);
  });
  url.searchParams.append('tgAuth', '1');
  history.pushState(null, '', url);
} else if (queryParams.get('tgAuth')) {
  url.searchParams.delete('tgAuth');
  history.pushState(null, '', url);
}

const hasQueryUtm =
  Boolean(queryParams.get('utm_source')) && Boolean(queryParams.get('utm_campaign'));

const _m = queryParams.get('_m');
if (_m && !hasQueryUtm) {
  queryParams.set('utm_source', 'models');
  queryParams.set('utm_campaign', _m);
}
const _a = queryParams.get('_a');
if (_a && !hasQueryUtm) {
  queryParams.set('utm_source', 'new_promo');
  queryParams.set('utm_campaign', _a);
}
const _modelUtm = queryParams.get('m');
if (_modelUtm && !hasQueryUtm) {
  queryParams.set('utm_source', 'marketing_models');
  queryParams.set('utm_campaign', _modelUtm);
}

if (queryParams.get('afterRegAction')) {
  setStorageItem('afterRegAction', queryParams.get('afterRegAction')!);
}

const lang = queryParams.get('lang');
if (lang && LanguageTags[lang]) {
  setStorageItem('lang', lang);
}

if (queryParams.get('ftg')) {
  // @ts-ignore
  window.isFromTelegramInApp = true;

  if (!queryParams.get('utm')) {
    statReachGoal('from_telegram_channel');
    try {
      queryParams.set('utm', 'ftg');
    } catch (e) {}
  }
}

if (queryParams.get('notify')) {
  // @ts-ignore
  window.isFromTelegramInAppV2 = true;
  statReachGoal('from_notify');
}

if (queryParams.get('notifier')) {
  statReachGoal('from_notifier');
}

const ref = queryParams.get('ref');
if (ref) {
  setStorageItem('ref', `${ref}`);
  statReachGoal('visit_by_ref', {
    ref,
  });
  sendRef(ref);
} else if (initialPath.substring(0, 5) === '/ref/') {
  const refFromPath = initialPath.substring(5);
  setStorageItem('ref', `${refFromPath}`);
  statReachGoal('visit_by_ref', {
    ref,
  });
  sendRef(refFromPath);
} else if (initialPath.substring(0, 3) === '/u/') {
  const refFromPath = initialPath.substring(3);
  setStorageItem('refDomain', `${refFromPath}`);
}

if (initialPath.substring(0, 3) === '/m/' && !hasQueryUtm) {
  queryParams.set('utm_source', 'marketing_users');
  queryParams.set('utm_campaign', initialPath.substring(3));
}

if (queryParams.get('code')) {
  // @ts-ignore
  window.VkAuthCode = queryParams.get('code');
} else {
  const hashParams = new URLSearchParams(
    '?' + String(window.location.hash || '').substr(1),
  );
  if (hashParams.get('access_token')) {
    // @ts-ignore
    window.VkAuthAccessToken = hashParams.get('access_token');
  }
}

const initUt = async () => {
  // Стабильный id устройства создаётся при ПЕРВОМ визите независимо от точки
  // входа: он живёт в localStorage и склеивает сессии (sendEvent шлёт его в
  // BigQuery). В utm он НЕ подмешивается: utm.visitorId уходит в utm_users и
  // включает affiliate-постбеки (postback.Send гейтится на len(VisitorId) > 0),
  // а их clickid — не наш uuid.
  const visitorId = ensureVisitorId();

  // Промо-ссылка пользователя (TPP-147): ?utm_slug=<slug>-<rand6>.
  // Токен фиксирует переход и отдаёт владельца — из него собираем привычные
  // utm_source/utm_campaign, чтобы регистрация и реферальные выплаты шли
  // по существующему пути без правок.
  const promoToken = queryParams.get('utm_slug');
  if (isPromoLinkToken(promoToken)) {
    const visit = await postPromoLinkVisit(promoToken!, visitorId);

    // Если utm_campaign уже задан (сеть дописала свои метки, mini-app deep link) —
    // визит выше всё равно записан, но чужую атрибуцию не перетираем.
    if (visit && !queryParams.get('utm_campaign')) {
      queryParams.set('utm_source', `user_${visit.ownerId}`);
      queryParams.set('utm_campaign', visit.campaign);

      // Дублируем в адресную строку: sendEvent (BigQuery) читает
      // window.location.search, иначе промо-трафик уедет как untagged.
      // Легаси-ссылки несут utm_* прямо в URL, так что это привычный вид.
      try {
        const u = new URL(location.href);
        u.searchParams.set('utm_source', `user_${visit.ownerId}`);
        u.searchParams.set('utm_campaign', visit.campaign);
        history.replaceState(null, '', u);
      } catch (e) {}
    }
  }

  const utm = await getUtmOnline(queryParams);

  if (utm && utm.type == 'url') {
    setUtmLocal(utm);
  }

  if (utm && (utm.type == 'url' || utm.type == 'local')) {
    await setUtmServer(utm);
  }

  window.gtag(
    'config',
    'G-8G5PX3ZGD9',
    utm && utm.campaign
      ? {
          campaign: {
            source: utm.source,
            name: utm.campaign,
          },
        }
      : {},
  );

  if (!getStorageItem('token')) {
    if (utm && (utm.source || utm.campaign)) {
      statReachGoal(`open_utm`, {
        campaign: utm.campaign,
        source: utm.source,
      });
    }
  }

  if (utm && utm.campaign) {
    // @ts-ignore
    window.isUtmUser = true;
  }
};

initUt().catch((e) => {
  console.log('init utm error', e);
});

const initFacebookPixel = () => {
  try {
    const utm = getUtmOffline(queryParams);
    if (utm?.campaign === 'mazency') {
      fbInit('1195553798797213');
    } else if (utm?.campaign === 'trafolka') {
      fbInit('806426165332952');
    } else {
      fbInit('');
    }
  } catch (e) {
    console.error('facebook pixel error', e);
  }
};
initFacebookPixel();

window.onerror = (event, source, lineno, colno, error) => {
  axios
    .post('https://api.tonplace.net/error', {
      error: JSON.stringify({
        event,
        source,
        lineno,
        colno,
        error,
        errorMsg: error?.message,
        url: location.href,
      }),
    })
    .then((r) => console.log('success capture error', r))
    .catch((e) => console.log('fail capture error', e));
};

try {
  router.start();
} catch (error) {
  if (error instanceof URIError) {
    const regex = /^(.*?ref=\d+)/;
    const newUrl = initialUrl.split(regex)[1];
    window.location.href = newUrl;
  } else {
    console.error(error);
  }
}

const handleError = (
  error: Error,
  info: {
    componentStack: string;
  },
) => {
  try {
    Sentry.captureReactException(error, info);
  } catch (e) {}

  statReachGoal('error', {
    message: error?.message ?? 'Empty',
    stack: error?.stack ?? 'Unknown',
  });
};

const FallbackComponent: FC<FallbackProps> = ({ error }) => {
  const t = useTranslator();
  const { key, label } = useCurrentLang();

  if (error.message.indexOf('Some parameters of ') === 0) {
    return <NotFound />;
  }

  if (error.name === 'ChunkLoadError') {
    return (
      <Placeholder
        centered
        title="New Update"
        actions={
          <Button
            onClick={() => {
              location.reload();
            }}
            size="small"
          >
            Refresh
          </Button>
        }
      >
        This application has been updated, please refresh your browser to see the latest
        content.
      </Placeholder>
    );
  }

  return (
    <Placeholder
      centered
      title="Error"
      actions={
        <Button
          onClick={() => {
            location.href = '/';
            location.reload();
          }}
          size="small"
        >
          Restart
        </Button>
      }
    >
      <div className="Error">
        <div className="Error__caption">
          Something went wrong. Please, restart the app.
        </div>
        <pre className="Error__code">{JSON.stringify(error.message)}</pre>
      </div>
    </Placeholder>
  );
};

if (!window.ResizeObserver) install();

ReactDOM.render(
  <React.StrictMode>
    {/* @ts-ignore */}
    <ErrorBoundary onError={handleError} FallbackComponent={FallbackComponent}>
      <RouterProvider router={router}>
        <TranslatorProvider>
          <ActionSheetProvider>
            <StoreProvider {...{ store }}>
              <App />
            </StoreProvider>
          </ActionSheetProvider>
        </TranslatorProvider>
      </RouterProvider>
    </ErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root'),
);

if (Object.values(tgAuth).length) {
  setStorageItem('telegramUserName', tgAuth.first_name || '');
  store.dispatch(authActions.telegramAuth({ params: tgAuth, botId: getTelegramBotId() }));
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/service-worker.js')
    .then((e) => {
      // e.update();
    })
    .catch((e) => {
      console.log('service worker registration failed', e);
    });
}

const skipPwaBanner = getStorageItem('skipPwaBanner') === 'true';

!isPWA() &&
  !skipPwaBanner &&
  window.addEventListener('beforeinstallprompt', (e) => {
    // @ts-ignore
    window.beforeInstallPrompt = e;
    e.preventDefault();
    store.dispatch(mainActions.setShowInstallPrompt(true));
  });

if (location.hostname === 'media.ton.place' || location.hostname === 'tonplace.io') {
  window.addEventListener('message', (e: any) => {
    const url = new URL(e.origin);
    if (url.hostname !== 'ton.place') {
      return;
    }

    if (e.data) {
      try {
        const data: any = JSON.parse(e.data);
        if (data.type === 'setToken') {
          setTokenRaw(data.payload);
        }
      } catch (e) {}
    }
  });
}
